import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { useChangeTier } from '~/hooks/apis/loyalty/useChangeTier';

import type { TierChangeRequestBody } from '~/types/loyaltyContract';

type TargetTier = TierChangeRequestBody['targetTier'];

const TIER_OPTIONS: Array<TargetTier> = ['silver', 'gold', 'platinum'];
const TIER_CHANGE_REMARK_SOURCE = 'ADMIN_PORTAL_MANUAL_UPGRADE';

export default function ChangeTierSection() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [customerId, setCustomerId] = useState('');
  const [targetTier, setTargetTier] = useState<TargetTier>('silver');
  const [reason, setReason] = useState('');

  const changeTierMutation = useChangeTier({
    onSuccess: () => {
      enqueueSnackbar('Tier changed successfully', { variant: 'success' });
      setCustomerId('');
      setTargetTier('silver');
      setReason('');
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to change tier: ${error.message}`, { variant: 'error' });
    },
  });

  const canSubmit = customerId.trim() && targetTier && reason.trim() && !changeTierMutation.isPending;

  const handleSubmit = () => {
    changeTierMutation.mutate({
      customerId: customerId.trim(),
      targetTier,
      reason: reason.trim(),
      remarkSource: TIER_CHANGE_REMARK_SOURCE,
    });
  };

  const tierPalette = theme.palette.loyaltyTiers[targetTier];
  const accentColor = tierPalette?.main ?? theme.palette.primary.main;

  return (
    <Card
      variant="outlined"
      sx={{
        borderTop: 3,
        borderTopColor: accentColor,
        transition: 'border-color 0.3s ease',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Change Tier
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Manually update a customer's société tier. This action takes effect immediately. Tier downgrades are not
          supported at this time.
        </Typography>

        <Stack spacing={4}>
          <TextField
            label="Customer ID"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            required
            fullWidth
            placeholder="e.g. abc123-def456-..."
          />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Target Tier *
            </Typography>
            <Stack direction="row" spacing={1.5}>
              {TIER_OPTIONS.map((tier) => {
                const palette = theme.palette.loyaltyTiers[tier];
                const color = palette?.main ?? theme.palette.grey[500];
                const isSelected = targetTier === tier;
                return (
                  <ButtonBase
                    key={tier}
                    onClick={() => setTargetTier(tier)}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      border: 2,
                      borderColor: isSelected ? color : 'divider',
                      bgcolor: isSelected ? alpha(color, 0.08) : 'transparent',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      '&:hover': {
                        borderColor: color,
                        bgcolor: alpha(color, 0.04),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: color,
                      }}
                    />
                    <Typography variant="subtitle2" fontWeight={isSelected ? 700 : 500}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </Typography>
                    {isSelected && <CheckCircleOutlineIcon sx={{ fontSize: 16, color }} />}
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder="Provide a reason for this tier change..."
          />

          <Divider />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              endIcon={
                changeTierMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />
              }
              sx={{ px: 4, py: 1.2 }}
            >
              {changeTierMutation.isPending ? 'Submitting...' : 'Change Tier'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
